import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../AuthProvider";
import { getApiToken } from "../authUtils";
import urlconfig from "../config/urlconfig";
import CostsheetDrillDialog, {getQuarter} from "./CostsheetDrillDailog";
import PartyDetailsPanel from "./PartyDetailsPanel";
import { ContactSummary, contactSearchText } from "../helpers/ContactDetailsFields";
import {
  CircularProgress,
  Alert,
  Paper,
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import BusinessIcon from "@mui/icons-material/Business";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color }) => (
  <Paper
    elevation={2}
    sx={{
      p: 3,
      borderRadius: 3,
      transition: "0.25s",
      "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
    }}
  >
    <Box display="flex" justifyContent="space-between">
      <Box>
        <Typography variant="h4" fontWeight="bold" color={color}>
          {value}
        </Typography>
        <Typography color="text.secondary">{label}</Typography>
      </Box>
      <Box color={color}>{icon}</Box>
    </Box>
  </Paper>
);

// ─── Item -> flat record transform (mirrors AnalyticsDashboard's processRaw, simplified) ─
const getCustomTotal = (rows, key) => rows?.reduce((sum, r) => sum + (parseFloat(r[key]) || 0), 0) || 0;

const transformItemsToRecords = (items) =>
  (items || []).map((entry) => {
    const subItems = entry.items || [];
    const subSp = subItems.reduce((s, i) => s + (Number(i.sp) || 0) * (Number(i.qty) || 0), 0);
    const subPp = subItems.reduce((s, i) => s + (Number(i.pp) || 0) * (Number(i.qty) || 0), 0);

    const customSp = getCustomTotal(entry.customRows, "customTotalSp");
    const customPp = getCustomTotal(entry.customRows, "customTotalPp");

    const totalSp =
      subSp +
      customSp +
      (Number(entry.transportationTotalSp) || 0) +
      (Number(entry.customizationTotalSp) || 0) +
      (Number(entry.marketingTotalSp) || 0);
    const totalPp =
      subPp +
      customPp +
      (Number(entry.transportationTotalPp) || 0) +
      (Number(entry.customizationTotalPp) || 0) +
      (Number(entry.marketingTotalPp) || 0);

    const margin = totalSp - totalPp;

    return {
      salesOrderNo: entry.salesOrderNo,
      clientName: entry.clientName,
      date: entry.date,
      quarter: entry.date ? getQuarter(entry.date) : "—",
      costsheetfor: entry.costsheetfor,
      totalSp,
      totalPp,
      margin,
      marginPercentage: totalSp > 0 ? (margin / totalSp) * 100 : 0,
    };
  });

const LegalVendorsDashboard = () => {
  const { instance } = useMsal();
  const { account } = useAuth();

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Drill-down state
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillTitle, setDrillTitle] = useState("");
  const [drillSubtitle, setDrillSubtitle] = useState("");
  const [drillRecords, setDrillRecords] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState(null);

  // The vendor whose drill-down is open, for the details panel inside it
  const [drillVendor, setDrillVendor] = useState(null);
  const [catalogs, setCatalogs] = useState([]);
  const [catalogsLoading, setCatalogsLoading] = useState(false);
  const [catalogsError, setCatalogsError] = useState(null);
  const [openingCatalogKey, setOpeningCatalogKey] = useState(null);

  const sendAuthenticatedRequest = useCallback(
    async (url) => {
      const token = await getApiToken(instance, account);
      const res = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text || "API failed");
      try {
        return text ? JSON.parse(text) : {};
      } catch {
        return text;
      }
    },
    [instance, account]
  );

  const fetchVendors = useCallback(async () => {
    if (!account) return;
    setLoading(true);
    setError(null);
    try {
      const data = await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/vendors`);
      setVendors(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [account, sendAuthenticatedRequest]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const stats = useMemo(() => {
    const total = vendors.length;
    const totalRawNames = vendors.reduce((sum, v) => sum + (v.rawVendorNames?.length || 0), 0);
    const unlinked = vendors.filter((v) => !(v.rawVendorNames?.length > 0)).length;
    return { total, totalRawNames, unlinked };
  }, [vendors]);

  const filteredVendors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vendors;
    return vendors.filter((v) => {
      const haystack = [
        v.legalVendorName,
        v.vendorGST,
        v.vendorState,
        contactSearchText(v, v.vendorSpoc, v.vendorSpocNumber),
        ...(v.rawVendorNames || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [vendors, search]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const paginatedVendors = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredVendors.slice(start, start + rowsPerPage);
  }, [filteredVendors, page, rowsPerPage]);

  // Catalogs uploaded for this vendor (newest first), shown in the drill-down's details panel
  const loadCatalogs = useCallback(
    async (vendor) => {
      setCatalogs([]);
      setCatalogsError(null);
      setCatalogsLoading(true);
      try {
        const params = new URLSearchParams({ vendorId: vendor.id, size: 100 });
        const page = await sendAuthenticatedRequest(
          `${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs?${params.toString()}`
        );
        setCatalogs(page?.content || []);
      } catch (err) {
        setCatalogsError(err.message || "Failed to load catalogs");
      } finally {
        setCatalogsLoading(false);
      }
    },
    [sendAuthenticatedRequest]
  );

  const openDrillDown = useCallback(
    async (vendor) => {
      setDrillVendor(vendor);
      loadCatalogs(vendor);
      setDrillTitle(vendor.legalVendorName);
      setDrillSubtitle(
        vendor.rawVendorNames?.length > 0
          ? `${vendor.rawVendorNames.length} raw name variant${vendor.rawVendorNames.length === 1 ? "" : "s"} · ${vendor.vendorGST || "—"}`
          : "No raw names mapped yet"
      );
      setDrillRecords([]);
      setDrillError(null);
      setDrillOpen(true);
      setDrillLoading(true);
      try {
        const items = await sendAuthenticatedRequest(
          `${urlconfig.backend_api_base_url}/api/v2/items/by-legal-vendor/${vendor.id}`
        );
        setDrillRecords(transformItemsToRecords(items));
      } catch (err) {
        setDrillError(err.message || "Failed to load cost sheets");
      } finally {
        setDrillLoading(false);
      }
    },
    [sendAuthenticatedRequest, loadCatalogs]
  );

  // urlField: "download-url" (catalog PDF) or "price-list-download-url"
  const openCatalogFile = useCallback(
    async (catalogId, urlField) => {
      setOpeningCatalogKey(`${catalogId}-${urlField}`);
      try {
        const data = await sendAuthenticatedRequest(
          `${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/${catalogId}/${urlField}`
        );
        window.open(data.url, "_blank", "noopener,noreferrer");
      } catch (err) {
        setCatalogsError(err.message || "Could not open the file");
      } finally {
        setOpeningCatalogKey(null);
      }
    },
    [sendAuthenticatedRequest]
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: "linear-gradient(135deg,#0f766e 0%,#2dd4bf 100%)",
          color: "white",
        }}
      >
        <Typography variant="h4" fontWeight={700}>
          Legal Vendors
        </Typography>
        <Typography sx={{ opacity: 0.9 }}>
          Registered vendors · Click any row to view all cost sheets across its mapped raw names
        </Typography>
      </Paper>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <StatCard icon={<BusinessIcon fontSize="large" />} label="Legal Vendors" value={stats.total} color="#0f766e" />
        </Grid>
        <Grid item xs={4}>
          <StatCard icon={<LinkIcon fontSize="large" />} label="Raw Vendor Names Mapped" value={stats.totalRawNames} color="#1976d2" />
        </Grid>
        <Grid item xs={4}>
          <StatCard icon={<ReceiptLongIcon fontSize="large" />} label="No Raw Vendor Names Yet" value={stats.unlinked} color="#ed6c02" />
        </Grid>
      </Grid>

      {/* Search */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search by name, GST, state, contact, or mapped raw name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Paper>

      <Divider sx={{ mb: 1 }} />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <>
          {filteredVendors.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No vendors match your search.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    <TableCell><b>Legal Vendor Name</b></TableCell>
                    <TableCell><b>GST</b></TableCell>
                    <TableCell><b>State</b></TableCell>
                    <TableCell><b>Contacts</b></TableCell>
                    <TableCell><b>SCM POC</b></TableCell>
                    <TableCell align="center"><b>Raw Names</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedVendors.map((v) => (
                    <TableRow
                      key={v.id}
                      hover
                      onClick={() => openDrillDown(v)}
                      sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography variant="body2" fontWeight={700}>
                            {v.legalVendorName}
                          </Typography>
                          <OpenInNewIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                        </Box>
                      </TableCell>
                      <TableCell>{v.vendorGST || "—"}</TableCell>
                      <TableCell>{v.vendorState || "—"}</TableCell>
                      <TableCell>
                        <ContactSummary contacts={v.contacts} legacyPerson={v.vendorSpoc} legacyPhone={v.vendorSpocNumber} />
                      </TableCell>
                      <TableCell>
                        <span style={{fontWeight:700, color:"#fff", backgroundColor:"#0f766e", padding:"2px 6px", borderRadius:"4px",}}>
                        {v.cScmPoc || "—"}
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        {v.rawVendorNames?.length > 0 ? (
                          <Chip
                            size="small"
                            label={v.rawVendorNames.length}
                            sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 700 }}
                          />
                        ) : (
                          <Chip size="small" label="0" sx={{ bgcolor: "#fff7ed", color: "#c2410c" }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={filteredVendors.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 250]}
          />
        </>
      )}

      {/* Drill-down dialog — same shared component used across the app */}
      {drillLoading ? (
        <Box
          sx={{
            display: drillOpen ? "flex" : "none",
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.4)",
            zIndex: 1300,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper sx={{ p: 4, borderRadius: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress size={32} />
            <Typography color="text.secondary">Loading cost sheets…</Typography>
          </Paper>
        </Box>
      ) : (
        <CostsheetDrillDialog
          open={drillOpen}
          onClose={() => setDrillOpen(false)}
          title={drillTitle}
          subtitle={
            drillError ? undefined : `${drillSubtitle} · ${drillRecords.length} cost sheet${drillRecords.length === 1 ? "" : "s"}`
          }
          records={drillRecords}
          details={
            <PartyDetailsPanel
              type="vendor"
              record={drillVendor}
              catalogs={catalogs}
              catalogsLoading={catalogsLoading}
              catalogsError={catalogsError}
              openingCatalogKey={openingCatalogKey}
              onOpenCatalogFile={openCatalogFile}
            />
          }
          showQoQ
        />
      )}
      {drillError && drillOpen && !drillLoading && (
        <Box sx={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 1400 }}>
          <Alert severity="error" onClose={() => setDrillError(null)}>
            {drillError}
          </Alert>
        </Box>
      )}
    </Paper>
  );
};

export default LegalVendorsDashboard;